import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import { loadWatch, loadLastReport, dispositionItem, runSweep, acceptCourt, attachSources, mergeIntoCard, setCourt, setPinned, moveSources, moveCard as moveCardApi, renameCard as renameCardApi, renameProject as renameProjectApi, createProject as createProjectApi, archiveProject as archiveProjectApi } from './watchApi';
import { dotState, agoLabel, countsLabel, isNewerThan, alertText } from './sweepStatus';
import { isNewItem, countBoard } from './counts';
import { parseDate, daysSinceTouched, effectiveWakeDate, isCold, isDone, linksOf, heat, heatTier, tier, TODAY_CAP, REASON_COLORS } from './tiers';
import { NowTray, TodayList } from './TierRow';
import CountStrip from './CountStrip';
import Brief from './Brief';
import NewRail, { rowFromItem, rowFromProposal } from './NewRail';
import StatusBlock, { hasStatus } from './StatusBlock';
import UndoToast from './UndoToast';
import ProjectsView, { ProjectSearch } from './ProjectsView';
import CheckRail, { checkKey } from './CheckRail';
import { createPending, UNDO_MS } from './pendingActions';
import MyList from './MyList';
import { loadMyList, myListAction, visibleTasks, listedWatchIds, ymdOf } from './myListApi';
import { emptyTree, buildProjectTree, projectColors as colorsOfTree, projectLabel, projectName, projectFilterValues, matchesProjectFilter, withProject, withoutProject, NO_PROJECT } from './projects';
import './Watch.css';

// ============================================================
// THE WATCH — round 2, live data.
// Reads Watch_Open / Watch_Sources / Watch_Meta via watchApi.
// Still no writes.
// ============================================================

function startOfToday() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

// child id -> parent id, from parent-of (authoritative) plus child-of backlinks
function buildChildToParent(items, byId) {
  const map = {};
  items.forEach(p => linksOf(p, 'parent-of').forEach(l => { if (byId.has(l.id)) map[l.id] = p.id; }));
  items.forEach(c => linksOf(c, 'child-of').forEach(l => { if (byId.has(l.id) && map[c.id] == null) map[c.id] = l.id; }));
  return map;
}

// Connected components over same-trip links; groups of 2+ become trip callouts.
function buildTripGroups(items, byId) {
  const parent = {};
  const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };
  items.forEach(i => { parent[i.id] = i.id; });
  items.forEach(i => linksOf(i, 'same-trip').forEach(l => { if (byId.has(l.id)) union(i.id, l.id); }));

  const groups = {};
  items.forEach(i => {
    if (linksOf(i, 'same-trip').length === 0 && !items.some(o => linksOf(o, 'same-trip').some(l => l.id === i.id))) return;
    const root = find(i.id);
    if (!groups[root]) groups[root] = [];
    groups[root].push(i);
  });

  return Object.values(groups).filter(g => g.length >= 2).map(g => {
    // Trip label: most common "why" on the group's same-trip links
    const whyCounts = {};
    g.forEach(i => linksOf(i, 'same-trip').forEach(l => {
      if (l.why) whyCounts[l.why] = (whyCounts[l.why] || 0) + 1;
    }));
    const label = Object.entries(whyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Linked trip';
    const dates = g.map(i => parseDate(i.dueDate)).filter(Boolean).sort((a, b) => a - b);
    return { label, items: g, firstDue: dates[0] || null };
  });
}

// Project colors and band captions come from the Watch_Projects tree
// (projects.js): id -> hex, id -> "PARENT › SUB". A card with no ProjectId
// keys on '' and gets the neutral slate. The old per-string palette is gone;
// the legacy Project text column is no longer read for display.

const KIND_COLORS = {
  Action: '#e6a817',
  Waiting: '#5a8de0',
  Decision: '#9a6be0',
  Clock: '#4ac0d0',
  Question: '#e0704a',
  Anchor: '#8a9aa8',
};

const SOURCE_ICONS = { email: '✉', call: '☎', meeting: '▣', portal: '⌗', note: '✎', doc: '▤' };

// Sources render oldest-first; the collapsed requester tag shows the newest.
function sortedSources(item) {
  return [...(item.sources || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
}

function latestSource(item) {
  const s = sortedSources(item);
  return s.length ? s[s.length - 1] : null;
}

// A source is a relay when the originator (requestedBy) differs from the
// sender (who). Sender = reply address; originator = whose requirement it is.
function isRelay(s) {
  return !!(s.requestedBy && s.requestedBy !== s.who);
}

// "marc@myfiginsurance.com" → "myfiginsurance" — the domain label is the
// account identity that matters for "which mailbox owns this thread".
function mailboxShort(mailbox) {
  if (!mailbox) return '';
  const domain = mailbox.split('@')[1] || mailbox;
  return domain.split('.')[0];
}

// Rebuild a mailbox-scoped OWA deep link. Graph's raw webLink opens in whichever
// account the browser session holds; with delegate rights the /mail/<upn>/ path
// pins the right mailbox instead.
const SWEEP_DEFAULT_MAILBOX = 'marc@myfiginsurance.com'; // what the sweep reads today
function replyUrl(s) {
  const mailbox = s.mailbox || SWEEP_DEFAULT_MAILBOX; // rows that predate the Mailbox column
  if (s.messageId) {
    return `https://outlook.office.com/mail/${encodeURIComponent(mailbox)}/deeplink/read/${encodeURIComponent(s.messageId)}`;
  }
  if (!s.webLink) return '';
  const m = /[?&]ItemID=([^&]+)/i.exec(s.webLink)          // classic owa/?ItemID= form
         || /\/deeplink\/read\/([^/?&]+)/i.exec(s.webLink); // newer deeplink form
  if (!m) return s.webLink;
  return `https://outlook.office.com/mail/${encodeURIComponent(mailbox)}/deeplink/read/${m[1]}`;
}

// "Alan de la Houssaye" → AD, "Grace Bruner" → GB, "Marc" → M.
// First word + second word (not last) so particle surnames initial correctly.
function initialsOf(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// Deterministic muted fill per sender — same person, same color, always.
const AVATAR_COLORS = ['#44566b', '#5b4a6b', '#46604f', '#6b5a44', '#5a6247', '#6b4a4a', '#455f66', '#5f5470'];

function avatarColor(name) {
  let h = 0;
  for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function fmtD(d) {
  if (!d) return '';
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function fmtDate(s) {
  return fmtD(parseDate(s));
}



// ============================================================
// CHECKLIST — structured multi-item asks. Outstanding = open,
// partial, or flagged (a flagged answer reads settled on paper but
// isn't); settled = everything else, moot and delivered included.
// ============================================================

const CHECKLIST_GLYPHS = { open: '○', answered: '✓', partial: '◐', delivered: '▣', moot: '⊘' };

function checklistCounts(list) {
  const total = list.length;
  const outstanding = list.filter(c => c.status === 'open' || c.status === 'partial' || c.flag).length;
  return { total, outstanding, settled: total - outstanding };
}

function Checklist({ list }) {
  const { total, outstanding, settled } = checklistCounts(list);
  return (
    <div className="Watch-checklist">
      <div className="Watch-cl-header">
        <span className="Watch-cl-progress">{settled} OF {total}</span>
        <div className="Watch-cl-bar">
          <div className="Watch-cl-bar-fill" style={{ width: `${(settled / total) * 100}%` }} />
        </div>
        {outstanding > 0 && <span className="Watch-cl-outstanding">{outstanding} OUTSTANDING</span>}
      </div>
      {list.map(c => (
        <div key={c.n} className={`Watch-cl-row Watch-cl-row--${c.status}`}>
          <span className="Watch-cl-glyph">{CHECKLIST_GLYPHS[c.status] || '•'}</span>
          <span className="Watch-cl-n">{c.n}</span>
          <div className="Watch-cl-body">
            <span className="Watch-cl-text">{c.text}</span>
            {c.answer && <span className="Watch-cl-answer">— {c.answer}</span>}
            {c.vaultPath && <div className="Watch-cl-vault">{c.vaultPath}</div>}
            {c.note && <div className="Watch-cl-note">{c.note}</div>}
            {c.flag && <div className="Watch-cl-flag">⚠ {c.flag}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// CARD
// ============================================================

function LinkLine({ link, byId }) {
  const target = byId.get(link.id);
  return (
    <div className="Watch-link-line">
      <span className="Watch-link-type">{link.type}</span>
      <span className="Watch-link-target">{target ? target.title : `#${link.id}`}</span>
      {link.why && <span className="Watch-link-why">— {link.why}</span>}
    </div>
  );
}

// wakeAfter may be an item-id reference (render the target's title) or legacy text.
function wakeAfterText(wakeAfter, byId) {
  if (typeof wakeAfter === 'number' && byId.get(wakeAfter)) return byId.get(wakeAfter).title;
  return wakeAfter;
}

// The card's class list. Border/glow = HEAT (stakes); the day counter =
// NEGLECT — independent signals. `inTier`: the card is open inside a NOW /
// TODAY row (TierRow), a light surface where the base 0.88 opacity of a
// heat-dim card reads as washed out — so no heat-dim there, full contrast;
// hot/warm/faint keep their glow. Tag/search dim and done still apply.
export function cardClasses({ item, heat = 0, inTier = false, selectedTags, searchQuery, projectText = '' }) {
  const isAnchor = item.kind === 'Anchor';
  const done = isDone(item);
  const classes = ['Watch-card'];
  if (hasStatus(item)) classes.push('Watch-card--synopsis');
  if (!isAnchor) {
    const t = heatTier(heat);
    if (!(inTier && t === 'dim')) classes.push('Watch-card--heat-' + t);
  }
  if (isAnchor) classes.push('Watch-card--anchor');
  if (inTier) classes.push('Watch-card--intier');
  // Done cards carry their own fixed opacity; tag dim never stacks on it.
  if (done) classes.push('Watch-card--done');
  else if (dimmedBySelection(item, selectedTags, searchQuery, projectText)) classes.push('Watch-card--tagdim');
  return classes;
}

export function Card({ item, today, byId, projectColors, projectLabels, showTouch, reasons, childItems, parentWhys, parentTag, heatMap, selectedTags, searchQuery, forceExpanded, inTier = false, onSuggestCourt }) {
  const [expanded, setExpanded] = useState(false);
  // D1.5: the synopsis is the card; the thread is the receipt, folded by default.
  const [threadOpen, setThreadOpen] = useState(false);
  // A Brief jump to a collapsed child opens its parent; the user can still
  // collapse it again afterwards — this only ever pushes open.
  useEffect(() => { if (forceExpanded) setExpanded(true); }, [forceExpanded]);
  const cold = isCold(item, today);
  const isAnchor = item.kind === 'Anchor';
  const projColor = projectColors?.[item.projectId || NO_PROJECT] || projectColors?.[NO_PROJECT] || '#4a5563';
  const projLabel = projectLabels?.[item.projectId || NO_PROJECT] || '—';
  const touched = daysSinceTouched(item, today);
  const childCount = childItems ? childItems.length : 0;
  const srcs = sortedSources(item);
  const latest = latestSource(item);
  const heat = (heatMap && heatMap[item.id]) || 0;
  // The card's right-hand group is right-aligned by hanging margin-left:auto on
  // whichever element leads it. Adding #id in front of HEAT moves that lead, so
  // name it once here rather than duplicating the condition in two classNames.
  // With no id (WatchId absent) this collapses to the pre-existing behaviour.
  const rightLead = (showTouch && touched != null) ? 'touch' : (item.id != null ? 'id' : 'heat');
  const blocksTargets = linksOf(item, 'blocks').map(l => byId.get(l.id)).filter(Boolean);
  const checklist = item.checklist || [];
  const clCounts = checklist.length > 0 ? checklistCounts(checklist) : null;
  const wakeEff = effectiveWakeDate(item);

  // Tag selection and search both dim, never hide — a non-matching card
  // keeps its spot. tagSelActive also drives the chip highlights below.
  const tagSelActive = (selectedTags || []).length > 0;
  const done = isDone(item);

  const classes = cardClasses({ item, heat, inTier, selectedTags, searchQuery, projectText: projLabel });

  return (
    <div className={classes.join(' ')} data-watch-id={item.id}
      style={{ '--proj-color': projColor }}>
      <div className="Watch-card-band">
        <span className="Watch-card-band-proj">{projLabel.toUpperCase()}</span>
        {parentTag && <span className="Watch-card-band-parent">◂ {parentTag}</span>}
      </div>
      <div className="Watch-card-top" onClick={() => setExpanded(e => !e)}>
        <div className="Watch-card-titlerow">
          {done ? (
            <span className="Watch-kind-badge Watch-kind-badge--done">
              {(item.status || '').toUpperCase()}
            </span>
          ) : (
            <span className="Watch-kind-badge" style={{ '--kind-color': KIND_COLORS[item.kind] || '#888' }}>
              {(item.kind || '?').toUpperCase()}
            </span>
          )}
          <span className={'Watch-card-title' + (done ? ' Watch-card-title--done' : '')}>
            {item.title}
            {childCount > 0 && <span className="Watch-child-count"> ({childCount})</span>}
          </span>
          <span className="Watch-card-caret">{expanded ? '▴' : '▾'}</span>
        </div>
        {!isAnchor && <StatusBlock item={item} today={today} onSuggestCourt={onSuggestCourt} />}
        <div className="Watch-card-metarow">
          {(item.tags || []).slice(0, 3).map(t => (
            <span key={t}
              className={'Watch-tag-chip' + (tagSelActive && selectedTags.includes(t) ? ' Watch-tag-chip--hit' : '')}>
              {String(t).toUpperCase()}
            </span>
          ))}
          {(item.tags || []).length > 3 && (
            <span className="Watch-tag-chip">+{item.tags.length - 3}</span>
          )}
          {reasons && reasons.map(r => (
            <span key={r} className="Watch-reason-chip" style={{ '--reason-color': REASON_COLORS[r] }}>{r}</span>
          ))}
          {item.dueDate && <span className="Watch-readout Watch-readout--due">DUE {fmtDate(item.dueDate)}</span>}
          {!item.dueDate && wakeEff && (
            <span className="Watch-readout Watch-readout--wake">
              WAKE {fmtD(wakeEff)}{item.wakeDate ? '' : ' · AUTO'}
            </span>
          )}
          {blocksTargets.length > 0 && (
            <span className="Watch-blocks-chip" title={blocksTargets.map(t => t.title).join(', ')}>
              ⛔ BLOCKS: {blocksTargets[0].title}{blocksTargets.length > 1 ? ` +${blocksTargets.length - 1}` : ''}
            </span>
          )}
          {Number(item.parkCount) >= 2 && (
            <span className="Watch-park-chip">PARKED ×{item.parkCount}</span>
          )}
          {!expanded && clCounts && (
            <span className={'Watch-cl-chip' + (clCounts.outstanding === 0 ? ' Watch-cl-chip--clear' : '')}>
              {clCounts.outstanding > 0
                ? `${clCounts.outstanding} OF ${clCounts.total} OUTSTANDING`
                : `ALL ${clCounts.total} SETTLED`}
            </span>
          )}
          {showTouch && touched != null && (
            <span className={'Watch-readout Watch-readout--touch' + (cold ? ' Watch-readout--hot' : '')}>{touched}d</span>
          )}
          {item.id != null && (
            <span className={'Watch-id-tag' + (rightLead === 'id' ? ' Watch-id-tag--solo' : '')}>#{item.id}</span>
          )}
          {isAnchor && item.confidence && <span className="Watch-confidence">{item.confidence.toUpperCase()}</span>}
          {latest && (
            <span className="Watch-req-tag">
              {SOURCE_ICONS[latest.kind] || '•'}{' '}
              {isRelay(latest)
                ? `${latest.requestedBy}${latest.requestedByOrg ? ` · ${latest.requestedByOrg}` : ''} (via ${latest.who})`
                : `${latest.who}${latest.org ? ` · ${latest.org}` : ''}`}
            </span>
          )}
        </div>
        {!expanded && item.nextAction && (
          <div className="Watch-card-next">{item.nextAction}</div>
        )}
      </div>

      {expanded && (
        <div className="Watch-card-detail">
          {item.nextAction && <div className="Watch-detail-row"><span className="Watch-detail-label">NEXT</span>{item.nextAction}</div>}
          {item.counterparty && <div className="Watch-detail-row"><span className="Watch-detail-label">WITH</span>{item.counterparty}</div>}
          {item.insuredName && <div className="Watch-detail-row"><span className="Watch-detail-label">INSURED</span>{item.insuredName}</div>}
          {item.person && <div className="Watch-detail-row"><span className="Watch-detail-label">PERSON</span>{item.person}</div>}
          {item.wakeAfter && <div className="Watch-detail-row"><span className="Watch-detail-label">WAKE AFTER</span>{wakeAfterText(item.wakeAfter, byId)}</div>}
          {item.notes && <div className="Watch-detail-row Watch-detail-notes">{item.notes}</div>}
          {checklist.length > 0 && <Checklist list={checklist} />}
          <div className="Watch-sources">
            {srcs.length === 0 ? (
              <div className="Watch-no-source">NO SOURCE — UNVERIFIED</div>
            ) : (
              <>
                <button type="button" className="Watch-thread-toggle" aria-expanded={threadOpen}
                  onClick={(e) => { e.stopPropagation(); setThreadOpen(o => !o); }}>
                  {threadOpen ? '▾' : '▸'} Thread ({srcs.length})
                </button>
                {threadOpen && srcs.map((s, i) => (
                  <div key={i} className="Watch-src-row">
                    <div className="Watch-src-avatar" style={{ background: avatarColor(s.who) }}>
                      {s.kind === 'email' ? initialsOf(s.who) : (SOURCE_ICONS[s.kind] || '•')}
                    </div>
                    <div className="Watch-src-body">
                      <div className="Watch-src-line">
                        <span className="Watch-src-who">{s.who}{s.org ? ` · ${s.org}` : ''}</span>
                        {s.isReAsk && <span className="Watch-src-reask">RE-ASK</span>}
                        {mailboxShort(s.mailbox) && (
                          <span className="Watch-src-mailbox">@{mailboxShort(s.mailbox)}</span>
                        )}
                        <span className="Watch-src-date">{fmtDate(s.date)}</span>
                      </div>
                      {isRelay(s) && (
                        <div className="Watch-src-line">
                          <span className="Watch-src-label Watch-src-label--obo">ON BEHALF OF</span>
                          <span className="Watch-src-originator">
                            {s.requestedBy}{s.requestedByOrg ? ` · ${s.requestedByOrg}` : ''}
                          </span>
                        </div>
                      )}
                      <div className="Watch-src-subject">{s.subject}
                        {s.subject && (
                          <button className="Watch-triage-btn Watch-src-copy" title="Copy subject for Outlook search"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(s.subject || '').then(() => { e.target.textContent = 'COPIED'; setTimeout(() => { e.target.textContent = 'COPY'; }, 1200); }); }}>COPY</button>
                        )}
                      </div>
                      {s.why && <div className="Watch-src-why">{s.why}</div>}
                      {s.isReplyTarget && (
                        <>
                          <button
                            className="Watch-src-reply"
                            disabled={!s.webLink}
                            title={s.webLink ? 'Open thread' : 'No link yet — find the thread by subject'}
                            onClick={() => { if (s.webLink) window.open(replyUrl(s), '_blank', 'noopener'); }}
                          >
                            REPLY TO {(s.who || '').toUpperCase()}{s.mailbox ? ` · ${mailboxShort(s.mailbox)}` : ''}
                          </button>
                          {s.mailbox && (
                            <div className="Watch-src-mailbox-hint">
                              Opens {s.mailbox} as delegate.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          {(item.links || []).length > 0 && (
            <div className="Watch-detail-links">
              {(item.links || []).map((l, i) => <LinkLine key={i} link={l} byId={byId} />)}
            </div>
          )}
          {childCount > 0 && (
            <div className="Watch-children">
              {childItems.map(c => (
                <div key={c.id} className="Watch-child-wrap">
                  <Card item={c} today={today} byId={byId} projectColors={projectColors} projectLabels={projectLabels}
                    showTouch={c.court === 'Theirs' || c.court === 'Holding'} heatMap={heatMap}
                    selectedTags={selectedTags} searchQuery={searchQuery} onSuggestCourt={onSuggestCourt} />
                  {parentWhys && parentWhys[c.id] && (
                    <div className="Watch-child-why">{parentWhys[c.id]}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TRIP CALLOUT
// ============================================================

function TripCallout({ group, today, projectColors }) {
  return (
    <div className="Watch-trip">
      <div className="Watch-trip-header">
        <span className="Watch-trip-tag">SAME TRIP</span>
        <span className="Watch-trip-label">{group.label}</span>
        <span className="Watch-trip-count">{group.items.length} STOPS{group.firstDue ? ` · ${fmtDate(group.firstDue.toISOString().slice(0, 10))}` : ''}</span>
      </div>
      <div className="Watch-trip-rows">
        {group.items.map(i => (
          <div key={i.id} className="Watch-trip-row">
            <span className="Watch-trip-dot" style={{ background: projectColors[i.projectId || NO_PROJECT] }} />
            <span className="Watch-trip-title">{i.title}</span>
            <span className="Watch-trip-court">{(i.court || '').toUpperCase()}</span>
            {i.nextAction && <span className="Watch-trip-next">{i.nextAction}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// BOARD SORT + FILTER — render-time only, seed never mutated.
// Sort applies within each court column, not across them.
// Filter chips OR within a field, AND across fields.
// ============================================================

const SORT_OPTIONS = [
  { key: 'seed', label: 'SEED ORDER' },
  { key: 'newest', label: 'NEWEST FIRST' },
  { key: 'due', label: 'DUE DATE' },
  { key: 'heat', label: 'HEAT' },
  { key: 'touched', label: 'LAST TOUCHED' },
  { key: 'title', label: 'TITLE' },
];

function sortColumn(colItems, sortKey, heatMap, today) {
  if (sortKey === 'seed') return colItems;
  const sorted = [...colItems];
  if (sortKey === 'newest') {
    // Ids are assigned in creation order — descending id = most recent first.
    sorted.sort((a, b) => b.id - a.id);
  } else if (sortKey === 'due') {
    // Dated items first, soonest first; blanks last in seed order.
    sorted.sort((a, b) => {
      const da = parseDate(a.dueDate), db = parseDate(b.dueDate);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });
  } else if (sortKey === 'heat') {
    sorted.sort((a, b) => (heatMap[b.id] || 0) - (heatMap[a.id] || 0));
  } else if (sortKey === 'touched') {
    // Oldest touch first; never-touched last.
    sorted.sort((a, b) => {
      const ta = daysSinceTouched(a, today), tb = daysSinceTouched(b, today);
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return tb - ta;
    });
  } else if (sortKey === 'title') {
    sorted.sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()));
  }
  return sorted;
}

const FILTER_FIELDS = ['projectId', 'kind', 'court', 'insuredName'];
const FILTER_LABELS = { projectId: 'PROJECT', kind: 'KIND', court: 'COURT', insuredName: 'INSURED' };
const EMPTY_FILTERS = { projectId: [], kind: [], court: [], insuredName: [] };

// Distinct values present in the seed, first-appearance order, per field.
// PROJECT chips are keyed on ProjectId, in tree order, with the blank chip
// ('' — cards with no project) last; the label comes from the tree.
function buildFilterValues(items, tree) {
  const map = {};
  FILTER_FIELDS.forEach(f => { map[f] = []; });
  items.forEach(i => FILTER_FIELDS.forEach(f => {
    if (f === 'projectId') return;
    const v = i[f];
    if (v != null && v !== '' && !map[f].includes(v)) map[f].push(v);
  }));
  map.projectId = projectFilterValues(items, tree);
  return map;
}

// A PROJECT chip matches the project and everything under it (projects.js).
function passesFilters(item, filters, tree) {
  return FILTER_FIELDS.every(f => filters[f].length === 0
    || (f === 'projectId' ? matchesProjectFilter(item, filters[f], tree) : filters[f].includes(item[f])));
}

// Free-text search — case-insensitive, whitespace-split terms ANDed as
// plain substrings over the card's text fields. Order-independent:
// every term must hit somewhere in the haystack. No regex, no fuzzy.
// Exported pure for testing (Watch.js itself can't be imported under
// plain node — JSX).
export function matchesSearch(item, q, projectText = '') {
  const query = (q || '').trim();
  if (!query) return true;
  const haystack = [
    item.title, item.notes, item.nextAction, item.counterparty,
    item.insuredName, projectText, ...(item.tags || []),
  ].join(' ').toLowerCase();
  return query.split(/\s+/).every(t => haystack.includes(t));
}

// A SLICE, not a conversion — correct only for the server's already-CT stamps
// (proving.ct, from toCT()). A genuine UTC stamp (anything Z-suffixed:
// report.sweptAt, the Watch_Meta watermark) needs ctStamp below, or the digits
// print five hours fast under a CT label.
const ctShort = (iso) => (iso || '').replace('T', ' ').slice(5, 16); // "08-26 16:20"
// UTC ISO → "AUG 31, 3:04 PM CT". The real conversion, for genuine UTC stamps.
const ctStamp = (iso) => {
  const d = iso ? new Date(iso) : null;
  return !d || isNaN(d.getTime()) ? '' :
    d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).toUpperCase() + ' CT';
};
const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
// Counterparty guess for ACCEPT COURT → Theirs: the proving message's sender
// name — or, when the sender is one of ours (outbound), the recipient name.
// prove() gains toName in the next server deploy; until then it's undefined
// and the '' fallback stands.
const guessCounterparty = (row) => {
  const p = row?.proving || {};
  if (/myfiginsurance|defineholy/i.test(p.from || '')) return p.toName || '';
  return p.fromName || '';
};

// Candidates for ATTACH TO …: Open items, same-insured first, then by
// project/title; filtered by a case-insensitive match across the fields a
// person would actually remember (title, project, insured, counterparty,
// person, tags, next action).
function attachCandidates(items, row, query, labelOf = () => '') {
  const q = (query || '').trim().toLowerCase();
  const ins = (row?.insuredName || '').toLowerCase();
  const hay = (i) => [i.title, labelOf(i), i.insuredName, i.counterparty, i.person, i.nextAction, ...(i.tags || [])]
    .filter(Boolean).join(' ').toLowerCase();
  return items
    .filter(i => i.status === 'Open')
    .filter(i => !q || hay(i).includes(q))
    .sort((a, b) => {
      const as = ins && (a.insuredName || '').toLowerCase() === ins ? 0 : 1;
      const bs = ins && (b.insuredName || '').toLowerCase() === ins ? 0 : 1;
      return as - bs || labelOf(a).localeCompare(labelOf(b)) || (a.title || '').localeCompare(b.title || '');
    })
    .slice(0, 8);
}

// Shared by board cards and rail rows: dim when any active selection
// mechanic (tags, search) misses. They compose by AND.
function dimmedBySelection(item, selectedTags, searchQuery, projectText = '') {
  const tagSelActive = (selectedTags || []).length > 0;
  const tagHit = !tagSelActive || (item.tags || []).some(t => selectedTags.includes(t));
  const searchActive = !!searchQuery;
  const searchHit = !searchActive || matchesSearch(item, searchQuery, projectText);
  return (tagSelActive && !tagHit) || (searchActive && !searchHit);
}

// ============================================================
// PAGE
// ============================================================

const COLUMNS = [
  { key: 'Mine', label: 'MY COURT' },
  { key: 'Theirs', label: 'THEIR COURT' },
  { key: 'Holding', label: 'HOLDING' },
  { key: 'chads', label: 'HANGING CHADS' },
];

export default function Watch() {
  const today = startOfToday();
  const { instance, accounts } = useMsal();

  const [items, setItems] = useState([]);
  // Phase A: the Watch_Projects tree, loaded with the board; the view
  // toggle (BOARD | PROJECTS) is session-only.
  // The flat Watch_Projects rows are the state (Phase B edits patch one
  // row); the tree is derived. A malformed list yields an empty tree and
  // the error line, never a crash.
  const [projectList, setProjectList] = useState([]);
  // Marc's List (My_List) — read with the board, written through the relay.
  const [myTasks, setMyTasks] = useState([]);
  const [myListError, setMyListError] = useState(null);
  const [projectsError, setProjectsError] = useState(null);
  const projects = useMemo(() => {
    try { return buildProjectTree(projectList); } catch (e) { return emptyTree(); }
  }, [projectList]);
  const [view, setView] = useState('board');
  // The PROJECTS search's target: { id, n } — n is fresh per pick so the
  // same card can be located twice.
  const [locate, setLocate] = useState(null);
  const [sweptAt, setSweptAt] = useState(null);
  // D2: the persisted SWEEP_LAST_REPORT (read with the board, and again by
  // CHECK NOW); when the board last started loading, so a report that
  // finished after that triggers a refetch; a minute tick for "N min ago".
  const [lastReport, setLastReport] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [checking, setChecking] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 60_000); return () => clearInterval(t); }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  // Row-level write failures only — load failures use `error` and the
  // full-page early return.
  const [actionError, setActionError] = useState(null);

  // ── Sweep (Stage 2) ──────────────────────────────────────────────────────
  const [sweep, setSweep] = useState(null);          // last report, dry or written
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepError, setSweepError] = useState(null);
  const [sweepArmed, setSweepArmed] = useState(false); // GO needs two clicks
  const [dismissed, setDismissed] = useState(new Set()); // rail row keys, local only
  // CHECK rail (routing (d)): the sweep's sender-only attaches. Live rows
  // come from the last run's result; on a page load the persisted report's
  // reduced { watchId, by } rows show instead. ✓ dismisses locally.
  const [checkDismissed, setCheckDismissed] = useState(new Set());
  const [attachFor, setAttachFor] = useState(null);   // proposed row key with picker open
  const [attachTarget, setAttachTarget] = useState('');
  const [attachQuery, setAttachQuery] = useState('');
  const [courtFor, setCourtFor] = useState(null);      // MOVED row watchId with the box open
  const [courtForm, setCourtForm] = useState({ counterparty: '', nextAction: '', wakeDate: '' });
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);   // BRIEF modal; read-only, derives from `items`

  const [expandReq, setExpandReq] = useState(null);    // { id, n }: parent a Brief jump forced open; n is fresh per jump
  // { text, ttl, onUndo?, key? }: a 2 s jump notice, or the 5 s undo strip
  // after PARK / KILL. Cleared by its own ttl; a newer toast replaces it.
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), toast.ttl || 2000);
    return () => clearTimeout(t);
  }, [toast]);
  // Held PARK / KILL writes (pendingActions.js). Lives across renders; a tab
  // close inside the window flushes every held write with keepalive.
  const pendingRef = useRef(null);
  if (!pendingRef.current) pendingRef.current = createPending({ delayMs: UNDO_MS });
  useEffect(() => {
    const flush = () => { pendingRef.current.flushAll({ keepalive: true }); };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    return () => { window.removeEventListener('pagehide', flush); window.removeEventListener('beforeunload', flush); };
  }, []);
  const closeBrief = useCallback(() => setBriefOpen(false), []);

  // ── Auth (same pattern as Finance.js) ───────────────────────────────────────
  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  // ID token for the Netlify relay (not the Graph access token — see
  // jarvisApi). forceRefresh covers a stale cached ID token on 401.
  const getIdToken = useCallback(async (forceRefresh) => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account, forceRefresh: !!forceRefresh });
    return res.idToken;
  }, [instance, accounts]);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const startedAt = Date.now();
      const token = await getToken();
      const [{ items, sweptAt, lastReport, projectList: plist, projectsError: perr }, ml] = await Promise.all([loadWatch(token), loadMyList(token)]);
      setItems(items);
      setMyTasks(ml.tasks);
      setMyListError(ml.error);
      setProjectList(plist || []);
      setProjectsError(perr || null);
      setSweptAt(sweptAt);
      setLastReport(lastReport);
      setLastLoadedAt(startedAt);
      setNowTick(Date.now());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // CHECK NOW — re-read the persisted report; reload the board only when a
  // run finished after the board last loaded. Never a sweep.
  const checkNow = async () => {
    setChecking(true);
    try {
      const rep = await loadLastReport(await getToken());
      if (isNewerThan(rep, lastLoadedAt)) await fetchData();
      setLastReport(rep);
      setNowTick(Date.now());
      setActionError(null);
    } catch (err) {
      setActionError(`check: ${err.message}`);
    } finally {
      setChecking(false);
    }
  };

  // NEW-rail disposition — optimistic local apply first, always. ACCEPT
  // then writes at once (it has its own strip). PARK and KILL hold the write
  // for UNDO_MS behind an undo toast: undo cancels the timer and puts the
  // previous item back in place (the row was replaced in the list, never
  // removed, so it returns to the same position with no network call). When
  // the timer fires the write runs exactly as before: replace with the
  // server's item (keeping local sources), revert and banner on failure,
  // busy only while the write is in flight. A second action on the same id
  // flushes the held one first, so "park then kill" lands both, in order.
  // PARK goes through the relay (watchApi.parkItem → /api/watch-park → App
  // Service /watch/park), which flips Triaged true, so a parked item leaves
  // the NEW rail like the other two verbs. Optimistic apply mirrors that.
  const localAfter = (item, action, payload) =>
    action === 'accept' ? { ...item, triaged: true, ...(payload?.court ? { court: payload.court } : {}) } :
    action === 'kill'   ? { ...item, triaged: true, status: 'Killed' } :
                          { ...item, triaged: true, court: 'Holding' };
  const commitDisposition = async (item, action, payload, opts = {}) => {
    const prev = item;
    setBusyIds(s => new Set(s).add(item.id));
    try {
      const updated = await dispositionItem(await getToken(), item, action, { getIdToken, payload, keepalive: opts.keepalive === true });
      setItems(list => list.map(i => (i.id === item.id ? { ...updated, sources: prev.sources, intake: prev.intake, synopsis: updated.synopsis ?? prev.synopsis } : i)));
      setActionError(null);
    } catch (err) {
      setItems(list => list.map(i => (i.id === item.id ? prev : i)));
      setActionError(`${item.title}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(item.id); return n; });
    }
  };
  const showUndo = (text, key) => setToast({
    key, text, ttl: UNDO_MS,
    onUndo: () => { pendingRef.current.undo(key); setToast(t => (t && t.key === key ? null : t)); },
  });
  const clearUndoToast = (key) => setToast(t => (t && t.key === key ? null : t));
  const disposition = async (item, action, payload) => {
    if (!item) return;
    const prev = item;
    setItems(list => list.map(i => (i.id === item.id ? localAfter(item, action, payload) : i)));
    if (action === 'accept') return commitDisposition(item, action, payload);
    const key = item.id;
    pendingRef.current.schedule(key, {
      run: (opts) => { clearUndoToast(key); return commitDisposition(item, action, payload, opts); },
      restore: () => setItems(list => list.map(i => (i.id === item.id ? prev : i))),
    });
    showUndo(action === 'park' ? 'Parked · Undo' : 'Killed · Undo', key);
  };
  // KILL ALL on the noise fold: one held batch, one timer, one undo.
  const killAll = (targets) => {
    const list0 = targets.filter(Boolean);
    if (!list0.length) return;
    const prevById = new Map(list0.map(i => [i.id, i]));
    setItems(list => list.map(i => (prevById.has(i.id) ? localAfter(i, 'kill') : i)));
    const key = `batch:${Date.now()}`;
    pendingRef.current.schedule(key, {
      ids: list0.map(i => i.id),
      run: async (opts) => { clearUndoToast(key); for (const it of list0) await commitDisposition(it, 'kill', undefined, opts); },
      restore: () => setItems(list => list.map(i => prevById.get(i.id) || i)),
    });
    showUndo(`Killed ${list0.length} · Undo`, key);
  };

  // "suggests: Theirs" on a card whose Court disagrees with the synopsis.
  const suggestCourt = async (item, court) => {
    setBusyIds(s => new Set(s).add(item.id));
    try {
      const updated = await setCourt(await getToken(), item, court);
      setItems(list => list.map(i => (i.id === item.id ? { ...updated, sources: item.sources, intake: item.intake, synopsis: item.synopsis } : i)));
      setActionError(null);
    } catch (err) {
      setActionError(`${item.title}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(item.id); return n; });
    }
  };

  // CHECK → MOVE: the source row(s) behind a sender-only attach go to the
  // card Marc picked — WatchId + RoutedBy 'attach' on each row. A live row
  // names the message (proving.internetMessageId); a reduced row from the
  // persisted report names only the tier, so its rows are the card's rows
  // stamped by that tier. Local state moves with them; the row is dismissed.
  const moveCheck = async (row, targetId) => {
    const from = items.find(i => i.id === row.watchId);
    const target = items.find(i => i.id === targetId);
    if (!from || !target) { setActionError(`#${row.watchId} → #${targetId}: not on the board`); return; }
    const imid = row.proving?.internetMessageId || '';
    const picked = (from.sources || []).filter(s => (imid ? s.internetMessageId === imid : s.routedBy === row.by));
    if (!picked.length) { setActionError(`#${row.watchId}: no source row to move`); return; }
    const key = checkKey(row);
    setBusyIds(s => new Set(s).add(from.id));
    try {
      const moved = await moveSources(await getToken(), picked, targetId);
      const gone = new Set(picked.map(s => s.spId));
      setItems(list => list.map(i =>
        i.id === from.id ? { ...i, sources: (i.sources || []).filter(s => !gone.has(s.spId)) } :
        i.id === targetId ? { ...i, sources: [...(i.sources || []), ...moved] } : i));
      setCheckDismissed(s => new Set(s).add(key));
      setActionError(null);
    } catch (err) {
      setActionError(`#${row.watchId} → #${targetId}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(from.id); return n; });
    }
  };
  const checkRows = ((sweep ? sweep.check : lastReport?.check) || []).filter(r => r && r.watchId && !checkDismissed.has(checkKey(r)));
  const checkReduced = !sweep;

  // "→ card #N" on a NEW item: its sources move onto card N and the item
  // closes as Triaged. The target gains the sources locally; no refetch.
  const mergeInto = async (item, targetId) => {
    const target = items.find(i => i.id === targetId);
    if (!item || !target) { setActionError(`#${targetId} not on the board`); return; }
    setBusyIds(s => new Set(s).add(item.id));
    try {
      const { item: closed, sources } = await mergeIntoCard(await getToken(), item, targetId);
      setItems(list => list.map(i =>
        i.id === item.id ? { ...closed, sources: item.sources, intake: item.intake } :
        i.id === targetId ? { ...i, sources: [...(i.sources || []), ...sources] } : i));
      setActionError(null);
    } catch (err) {
      setActionError(`${item.title} → #${targetId}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(item.id); return n; });
    }
  };

  // PIN — holds a NOW slot (tiers.js). Optimistic, reverted on failure.
  const togglePin = async (item) => {
    if (!item) return;
    const prev = item;
    const next = !item.pinned;
    setBusyIds(s => new Set(s).add(item.id));
    setItems(list => list.map(i => (i.id === item.id ? { ...i, pinned: next } : i)));
    try {
      const updated = await setPinned(await getToken(), item, next);
      setItems(list => list.map(i => (i.id === item.id ? { ...updated, sources: prev.sources, intake: prev.intake, synopsis: updated.synopsis ?? prev.synopsis } : i)));
      setActionError(null);
    } catch (err) {
      setItems(list => list.map(i => (i.id === item.id ? prev : i)));
      setActionError(`${item.title}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(item.id); return n; });
    }
  };

  // ── Phase B: project edits, each held behind the undo toast ───────────
  // One pending write per key (a card id, or proj:<id>): the optimistic
  // state applies at once, the write fires when the 5 s window closes, undo
  // puts the previous state back with no network call, and a second action
  // on the same key flushes the first (pendingActions.js). Failure reverts
  // and banners, exactly as PARK / KILL do.
  const keepLocal = (updated, prev) => ({ ...updated, sources: prev.sources, intake: prev.intake, synopsis: updated.synopsis ?? prev.synopsis });
  const holdWrite = ({ key, label, apply, revert, run }) => {
    apply();
    pendingRef.current.schedule(key, {
      run: async () => {
        clearUndoToast(key);
        try { await run(); setActionError(null); }
        catch (err) { revert(); setActionError(`${label}: ${err.message}`); }
      },
      restore: revert,
    });
    showUndo(`${label} · Undo`, key);
  };
  // MOVE — ProjectId + ProjectLocked. '' = Marc chose "no project".
  const moveCard = (item, projectId) => {
    if (!item) return;
    const prev = item;
    holdWrite({
      key: item.id, label: `Moved to ${projectId ? projectName(projects, projectId) : 'no project'}`,
      apply: () => setItems(list => list.map(i => (i.id === item.id ? { ...i, projectId: projectId || '', projectLocked: true } : i))),
      revert: () => setItems(list => list.map(i => (i.id === item.id ? prev : i))),
      run: async () => { const updated = await moveCardApi(await getToken(), item, projectId); setItems(list => list.map(i => (i.id === item.id ? keepLocal(updated, prev) : i))); },
    });
  };
  // RENAME a card — Title + TitleLocked.
  const renameCard = (item, title) => {
    if (!item || !title) return;
    const prev = item;
    holdWrite({
      key: item.id, label: 'Renamed',
      apply: () => setItems(list => list.map(i => (i.id === item.id ? { ...i, title, titleLocked: true } : i))),
      revert: () => setItems(list => list.map(i => (i.id === item.id ? prev : i))),
      run: async () => { const updated = await renameCardApi(await getToken(), item, title); setItems(list => list.map(i => (i.id === item.id ? keepLocal(updated, prev) : i))); },
    });
  };
  // MERGE — the same write as the rail's "→ card #N" chip, held: sources
  // move onto the target, the card closes with a "merged into #N" note.
  const mergeCard = (item, targetId) => {
    const target = items.find(i => i.id === targetId);
    if (!item || !target) { setActionError(`#${targetId} not on the board`); return; }
    const prevItem = item, prevTarget = target;
    holdWrite({
      key: item.id, label: `Merged into #${targetId}`,
      apply: () => setItems(list => list.map(i =>
        i.id === item.id ? { ...i, status: 'Closed', triaged: true } :
        i.id === targetId ? { ...i, sources: [...(i.sources || []), ...(item.sources || [])] } : i)),
      revert: () => setItems(list => list.map(i => (i.id === item.id ? prevItem : i.id === targetId ? prevTarget : i))),
      run: async () => {
        const { item: closed, sources } = await mergeIntoCard(await getToken(), item, targetId);
        setItems(list => list.map(i =>
          i.id === item.id ? { ...closed, sources: item.sources, intake: item.intake } :
          i.id === targetId ? { ...prevTarget, sources: [...(prevTarget.sources || []), ...sources] } : i));
      },
    });
  };
  // RENAME a project — one Watch_Projects row; every card follows by id.
  const renameProject = (node, name) => {
    if (!node || !name) return;
    const prev = projectList.find(p => p.id === node.id);
    if (!prev) return;
    holdWrite({
      key: `proj:${node.id}`, label: `Renamed to ${name}`,
      apply: () => setProjectList(list => withProject(list, { ...prev, name })),
      revert: () => setProjectList(list => withProject(list, prev)),
      run: async () => { const updated = await renameProjectApi(await getToken(), prev, name); setProjectList(list => withProject(list, { ...prev, ...updated })); },
    });
  };
  // NEW-rail chip pick (Phase C): written at once — the card exists, it just
  // is not triaged — so the chip goes solid and a later ACCEPT / PARK sees
  // the lock already in place. '' = "No project": clears and locks.
  const setRowProject = async (row, projectId) => {
    const item = items.find(i => i.id === row.id);
    if (!item) return;
    const prev = item;
    setItems(list => list.map(i => (i.id === item.id ? { ...i, projectId: projectId || '', projectLocked: true } : i)));
    try {
      const updated = await moveCardApi(await getToken(), item, projectId);
      setItems(list => list.map(i => (i.id === item.id ? keepLocal(updated, prev) : i)));
      setActionError(null);
    } catch (err) {
      setItems(list => list.map(i => (i.id === item.id ? prev : i)));
      setActionError(`${item.title}: ${err.message}`);
    }
  };
  // CREATE a project — written at once (a card can only be filed under a
  // real id), then a 5 s toast whose Undo ARCHIVES the new row.
  const createProject = async ({ name, parentId = '', color = '' }) => {
    if (!name) return;
    try {
      const created = await createProjectApi(await getToken(), { name, parentId, color });
      setProjectList(list => withProject(list, created));
      setActionError(null);
      const key = `proj:${created.id}`;
      setToast({
        key, text: `Created ${created.name} · Undo`, ttl: UNDO_MS,
        onUndo: async () => {
          setToast(t => (t && t.key === key ? null : t));
          setProjectList(list => withoutProject(list, created.id));
          try { await archiveProjectApi(await getToken(), created); }
          catch (err) { setProjectList(list => withProject(list, created)); setActionError(`Undo create: ${err.message}`); }
        },
      });
    } catch (err) {
      setActionError(`Create ${name}: ${err.message}`);
    }
  };

  // ── Rail row keys ──────────────────────────────────────────────────────
  // One definition per rail, used by the header count, the row filter, and the
  // React key, so a count can never drift from the rows under it. These are the
  // same prefixes DISMISS writes into `dismissed`.
  const railKey = {
    moved:     r => `m:${r.watchId}`,
    closed:    r => `c:${r.watchId}:${r.proving?.internetMessageId}`,
    proposed:  r => `p:${r.key}`,
    unmatched: r => `u:${r.key}`,
  };
  const shownRows = (kind) => (sweep?.[kind] || []).filter(r => !dismissed.has(railKey[kind](r)));

  // ── Dismissal, adjusted ────────────────────────────────────────────────
  // DISMISS used to be a render filter only. These three derive the write-side
  // truth from it. All of them read `sweep.proposed` rather than `dismissed`
  // directly: that drops the m:/c:/u: prefixes (those rails are display-only by
  // design, and the server's exclude guard sits in the unmatched branch where
  // they never reach it) and any p: key left over from an earlier report.
  const dismissedProposedKeys = (sweep?.proposed || [])
    .filter(r => dismissed.has(railKey.proposed(r)))
    .map(r => r.key);
  // proposed[] and the server's newOpens[] are pushed by the same statement
  // pair, so plan.newOpens === proposed.length and over-subtraction cannot
  // happen. The clamp is free defense, not load-bearing.
  const adjNewOpens = Math.max(0, (sweep?.plan?.newOpens || 0) - dismissedProposedKeys.length);
  // LABEL ONLY — deliberately NOT in adjPlanTotal below. plan.sources has
  // always excluded proposed-thread sources: the server's sourceWrites loop
  // lives in the matched branch, while a new Open's sources are created per
  // n.msgs inside the write loop. So this fixes a pre-existing under-report,
  // it is not compensation for the exclude work. r.messages is that thread's
  // tmsgs.length — exactly what the write loop iterates — so the sum is exact.
  const adjSources = (sweep?.plan?.sources || 0) +
    shownRows('proposed').reduce((n, r) => n + (r.messages || 0), 0);
  // Gate total. Keeps plan.sources, NOT adjSources: folding the proposal
  // sources in here would keep GO alive off the very proposals the operator
  // just dismissed, which is the bug this is fixing.
  const adjPlanTotal = (sweep?.plan?.sources || 0) + (sweep?.plan?.lastTouched || 0) + adjNewOpens;

  // report.mailbox is joined across every enabled mailbox ("a@x, b@y"), so it
  // is not a UPN and must not be pasted into an OWA path. Treat a joined value
  // as unknown and let replyUrl fall back to SWEEP_DEFAULT_MAILBOX. The real
  // fix is server-side: prove()/pickMsg drop the per-message m.mailbox the
  // sweep already has. attachSources still gets the raw value on purpose —
  // that is a fact write, not a display guess.
  const sweepMailbox = (sweep?.mailbox || '').includes(',') ? '' : (sweep?.mailbox || '');

  const doSweep = async (dryRun, keepDismissed = false) => {
    setSweepBusy(true); setSweepError(null); setSweepArmed(false);
    try {
      // exclude rides the write run only. A fresh dry sweep clears `dismissed`
      // (below), so sending the pre-clear set there would exclude threads the
      // operator is about to un-dismiss; the keepDismissed rescan does not need
      // it either, since ATTACH has already given that thread a WatchId and the
      // guard only fires on unmatched threads.
      const report = await runSweep(getIdToken, {
        dryRun,
        ...(dryRun ? {} : { exclude: dismissedProposedKeys }),
      });
      if (dryRun) {
        setSweep(report);
        if (!keepDismissed) setDismissed(new Set());
        setAttachFor(null); setAttachQuery('');
      } else {
        // Facts landed — reload the board from SharePoint, drop the rail.
        setSweep(null);
        await fetchData();
      }
    } catch (err) {
      setSweepError(err.message);
    } finally {
      setSweepBusy(false);
    }
  };

  const dismissRow = (key) => setDismissed(s => new Set(s).add(key));

  // ACCEPT COURT — two steps: openAcceptCourt shows the who/what/when box,
  // commitAcceptCourt writes Court + whatever was filled in. LastTouched
  // still belongs to the write-sweep.
  const openAcceptCourt = (row) => {
    const item = items.find(i => i.id === row.watchId);
    setCourtFor(row.watchId);
    setCourtForm({
      counterparty: row.computedCourt === 'Theirs' ? (guessCounterparty(row) || item?.counterparty || '') : (item?.counterparty || ''),
      nextAction: '',
      wakeDate: row.computedCourt === 'Theirs' ? plusDays(7) : '',
    });
  };

  const commitAcceptCourt = async (row, extra) => {
    const item = items.find(i => i.id === row.watchId);
    if (!item) { setSweepError(`#${row.watchId} not on the board`); return; }
    const key = `m:${row.watchId}`;
    setBusyIds(s => new Set(s).add(item.id));
    try {
      const updated = await acceptCourt(await getToken(), item, row.computedCourt, extra);
      const merged = { ...updated, sources: item.sources };
      if (extra.counterparty) merged.counterparty = extra.counterparty;
      if (extra.nextAction) merged.nextAction = extra.nextAction;
      if (extra.wakeDate) merged.wakeDate = extra.wakeDate;
      setItems(list => list.map(i => (i.id === item.id ? merged : i)));
      dismissRow(key); setCourtFor(null);
      await doSweep(true, true);
    } catch (err) {
      setSweepError(`${item.title}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(item.id); return n; });
    }
  };

  // ATTACH TO … — writes the thread's messages as sources on an existing
  // Open. Do this BEFORE go, or the write-sweep creates a duplicate Open
  // (Runbook §5d step 4).
  const doAttach = async (row, explicitId) => {
    const watchId = Number(explicitId || attachTarget);
    const item = items.find(i => i.id === watchId);
    if (!item) return;
    const key = `p:${row.key}`;
    setBusyIds(s => new Set(s).add(item.id));
    try {
      const added = await attachSources(await getToken(), watchId, row.msgs || [], sweep.mailbox);
      setItems(list => list.map(i => (i.id === item.id ? { ...i, sources: [...(i.sources || []), ...added] } : i)));
      dismissRow(key); setAttachFor(null); setAttachTarget(''); setAttachQuery('');
      await doSweep(true, true);
    } catch (err) {
      setSweepError(`attach → #${watchId}: ${err.message}`);
    } finally {
      setBusyIds(s => { const n = new Set(s); n.delete(item.id); return n; });
    }
  };

  const [todayOpen, setTodayOpen] = useState(true);
  const [backlogOpen, setBacklogOpen] = useState(false);   // BACKLOG disclosure, session only
  const [tierExpanded, setTierExpanded] = useState(null);  // the one NOW/TODAY row open to its card
  const [sortKey, setSortKey] = useState('seed');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showDone, setShowDone] = useState(false);

  // Masthead sweep line (D2): the persisted report's finish time, relative;
  // the watermark alone when no report exists yet. Colour carries the state.
  const dot = dotState(lastReport, new Date(nowTick));
  const sweepAgo = agoLabel(lastReport?.finishedAt || lastReport?.sweptAt || sweptAt, new Date(nowTick));
  const sweepCounts = countsLabel(lastReport);
  const sweepAlert = alertText(lastReport);
  const byId = new Map(items.map(i => [i.id, i]));
  const childToParent = buildChildToParent(items, byId);
  const projectColors = colorsOfTree(projects);
  const projectLabels = { [NO_PROJECT]: '—' };
  for (const id of projects.byId.keys()) projectLabels[id] = projectLabel(projects, id);
  const labelOf = (i) => projectLabels[i?.projectId || NO_PROJECT] || '—';
  const colorOf = (i) => projectColors[i?.projectId || NO_PROJECT];
  const tripGroups = buildTripGroups(items, byId);

  const heatMap = {};
  items.forEach(i => { heatMap[i.id] = heat(i, byId, items, today); });

  // Children whose court differs from their parent's ESCAPE the collapse —
  // they render in their own court column with a parent tag. The ball being
  // in someone else's hands outranks hierarchy.
  const escaped = new Set();
  Object.entries(childToParent).forEach(([cid, pid]) => {
    const c = byId.get(Number(cid));
    const p = byId.get(pid);
    if (c && p && c.court !== p.court) escaped.add(c.id);
  });

  // Parents map: parent id -> collapsed (same-court) child items + per-child why
  const childrenOf = {};
  const parentWhysOf = {};
  items.forEach(p => {
    const kids = linksOf(p, 'parent-of').map(l => byId.get(l.id))
      .filter(Boolean).filter(k => !escaped.has(k.id));
    if (kids.length) {
      childrenOf[p.id] = kids;
      parentWhysOf[p.id] = {};
      linksOf(p, 'parent-of').forEach(l => { if (l.why) parentWhysOf[p.id][l.id] = l.why; });
    }
  });

  // Parent tag for any child rendered outside its parent's collapse.
  const parentTagOf = (i) => {
    const pid = childToParent[i.id];
    return pid != null && escaped.has(i.id) ? (byId.get(pid)?.title || null) : null;
  };

  // NEW — untriaged arrivals awaiting disposition. A second view: these
  // items keep their board columns.
  const newItems = items.filter(isNewItem);
  // Masthead count strip — from the items in memory, so CHECK NOW's reload
  // refreshes it. New reveals the rail (it ignores filters); the court
  // segments toggle the same filter the chips below show and clear.
  const boardCounts = useMemo(() => countBoard(items), [items]);
  const newRailRef = useRef(null);
  const [railPulse, setRailPulse] = useState(0);
  const revealNewRail = () => {
    newRailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setRailPulse(n => n + 1);
  };
  useEffect(() => {
    if (!railPulse) return undefined;
    const t = setTimeout(() => setRailPulse(0), 1000);
    return () => clearTimeout(t);
  }, [railPulse]);

  // TODAY — live items, plus anything with heat >= 70 regardless of
  // cold/due/wake. Sorted by heat descending: hottest first, rotting or not.
  // BOARD — same-court children render under their parent; escaped children
  // are full board citizens (court column, or Chads when cold).
  // Marc's List: a card an open task points at carries `listed`, which
  // tiers.js ranks right after pins in NOW (his list beats the heat score).
  const listedIds = listedWatchIds(myTasks);
  const boardItems = items.filter(i => childToParent[i.id] == null || escaped.has(i.id)).map(i => (listedIds.has(i.id) ? { ...i, listed: true } : i));

  // BRIEF row click → land on the card. If it is already in the DOM, just
  // scroll + flash (no state touched). Otherwise clear whatever is hiding
  // it — chip filters, tags, search, SHOW DONE, a collapsed parent — let
  // React re-render (two frames), then find it. Still missing = data error,
  // say so rather than fail silently.
  const findCard = (id) => document.querySelector(`.Watch-card[data-watch-id="${id}"]`);
  // Land on a card: centre it, pulse it three times, then leave a gold
  // left-edge bar for a few seconds so a late-arriving eye still finds it.
  // A child card inside a parent loop pulses itself; the parent only gets a
  // one-time border tint so the loop is identifiable without competing.
  // Timings mirror Brief.css (pulse 6 × 0.6s, bar lingers 4s after).
  const revealCard = (el) => {
    const PULSE_MS = 3600, LINGER_MS = 4000, TINT_MS = 2400;
    const parent = el.parentElement?.closest('.Watch-card') || null;
    const swap = (node, cls, on) => node && node.classList[on ? 'add' : 'remove'](cls);
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    ['Watch-card--pulse', 'Watch-card--here'].forEach(c => swap(el, c, false));
    swap(parent, 'Watch-card--loop-tint', false);
    void el.offsetWidth; // restart animations if a previous jump is mid-flight
    swap(el, 'Watch-card--pulse', true);
    swap(el, 'Watch-card--here', true);
    swap(parent, 'Watch-card--loop-tint', true);
    setTimeout(() => swap(el, 'Watch-card--pulse', false), PULSE_MS);
    setTimeout(() => swap(parent, 'Watch-card--loop-tint', false), TINT_MS);
    setTimeout(() => swap(el, 'Watch-card--here', false), PULSE_MS + LINGER_MS);
  };
  // ── Marc's List handlers ────────────────────────────────────────────
  // check / uncheck / drop: optimistic row first, write held behind the undo
  // toast (same pendingActions as PARK / KILL). add and note write at once
  // and read the server's row back. Every write goes through the relay so
  // the card side (Notes line, note source row, synopsis re-read) happens.
  const todayYmd = ymdOf(today);
  const patchTask = (taskId, patch) => setMyTasks(list => list.map(t => (t.taskId === taskId ? { ...t, ...patch } : t)));
  const heldTaskWrite = (task, action, patch, label) => {
    const prev = task;
    patchTask(task.taskId, patch);
    const key = `ml:${task.taskId}`;
    pendingRef.current.schedule(key, {
      run: async (opts) => {
        clearUndoToast(key);
        try {
          const r = await myListAction(getIdToken, { action, taskId: task.taskId }, opts);
          if (r?.task) patchTask(task.taskId, r.task);
          if (r?.card?.closed) await fetchData();          // "close card" — the board changed
          setActionError(null);
        } catch (err) {
          patchTask(task.taskId, prev);
          setActionError(`list ${action}: ${err.message}`);
        }
      },
      restore: () => patchTask(task.taskId, prev),
    });
    showUndo(label, key);
  };
  const myListCheck = (task) => heldTaskWrite(task, 'check', { status: 'Done', doneAt: todayYmd }, 'Done · Undo');
  const myListUncheck = (task) => heldTaskWrite(task, 'uncheck', { status: 'Open', doneAt: null }, 'Reopened · Undo');
  const myListDrop = (task) => heldTaskWrite(task, 'drop', { status: 'Dropped' }, 'Dropped · Undo');
  const myListNote = async (task, note) => {
    pendingRef.current.flush(`ml:${task.taskId}`);
    try {
      const r = await myListAction(getIdToken, { action: 'note', taskId: task.taskId, note });
      if (r?.task) patchTask(task.taskId, r.task);
      setActionError(null);
    } catch (err) { setActionError(`list note: ${err.message}`); throw err; }
  };
  const myListAdd = async (payload) => {
    const r = await myListAction(getIdToken, payload);
    if (r?.task) {
      setMyTasks(list => [...list.filter(t => t.taskId !== r.task.taskId), r.task]);
      if (r.newCard) await fetchData();                    // the new card has to show on the board
    }
    return r;
  };
  const myVisible = visibleTasks(myTasks, { today: todayYmd });
  const pickerProjects = projectList.filter(p => p.status !== 'Archived').map(p => ({ projectId: p.id, label: projectLabel(projects, p.id) }));
  const colorOfProject = (pid) => projectColors[pid || NO_PROJECT];

  const jumpToCard = (id) => {
    setBriefOpen(false);
    if (view !== 'board') setView('board');   // the card lives on the board; the projects view only opens rows on click
    const visible = findCard(id);
    if (visible) { requestAnimationFrame(() => revealCard(visible)); return; }

    const item = byId.get(id);
    setFilters(EMPTY_FILTERS);
    setSelectedTags([]);
    setSearchText('');
    if (item && isDone(item)) setShowDone(true);
    const pid = childToParent[id];
    if (pid != null && !escaped.has(id)) setExpandReq({ id: pid, n: Date.now() });

    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = findCard(id);
      if (el) { revealCard(el); return; }
      setToast({ text: `#${id} not on board`, ttl: 2000 });
    }));
  };

  // SORT + FILTER — render-time only. A parent stays rendered when any of
  // its collapsed children passes, so the child keeps its header.
  const filterValues = buildFilterValues(items, projects);
  const toggleFilter = (field, value) => setFilters(prev => ({
    ...prev,
    [field]: prev[field].includes(value)
      ? prev[field].filter(v => v !== value)
      : [...prev[field], value],
  }));
  const boardVisible = (i) =>
    passesFilters(i, filters, projects) ||
    (childrenOf[i.id] || []).some(k => passesFilters(k, filters, projects));

  // Board-only status filter: Closed/Killed hide unless SHOW DONE is on.
  // NOW, TODAY, NEW, and SAME TRIP are unaffected. Applies to collapsed
  // children as well as columns — a hidden status hides everywhere on
  // the board.
  const statusShown = (i) => showDone || i.status === 'Open';
  // D3 tiers over the filtered board (top-level cards; children stay under
  // their parent): NOW / TODAY / BACKLOG. Filters narrow all three, so NOW
  // may hold fewer than three while a chip is on.
  const tiers = tier(boardItems.filter(boardVisible), { heatMap, today });
  const tierIds = new Set([...tiers.now, ...tiers.today].map(e => e.item.id));
  // The Projects view badges NOW / TODAY from the UNFILTERED board — a
  // project chip must never change which cards are live.
  const tiersAll = tier(boardItems, { heatMap, today });
  const tierOfAll = {};
  tiersAll.now.forEach(e => { tierOfAll[e.item.id] = 'NOW'; });
  tiersAll.today.forEach(e => { tierOfAll[e.item.id] = 'TODAY'; });
  const tierOf = (id) => tierOfAll[id];
  const visibleChildrenOf = (id) => {
    const kids = (childrenOf[id] || []).filter(statusShown);
    return kids.length ? kids : undefined;
  };
  const visibleCount = boardItems.filter(statusShown).filter(boardVisible).length;

  // TAGS — independent of the filters above. Selection dims, never hides.
  const allTags = [...new Set(items.flatMap(i => i.tags || []))].sort();
  const toggleTag = (t) => setSelectedTags(prev =>
    prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const tagMatchCount = selectedTags.length > 0
    ? items.filter(i => (i.tags || []).some(t => selectedTags.includes(t))).length
    : 0;

  // SEARCH — same dim-never-hide mechanic as tags, independent of it.
  const searchQuery = searchText.trim().toLowerCase();
  const searchMatchCount = searchQuery
    ? items.filter(i => matchesSearch(i, searchQuery, labelOf(i))).length
    : 0;

  // Done items the status filter is holding off the board right now —
  // non-Open items that pass the active chip filters. Feeds the SHOW DONE
  // HIDDEN/SHOWN readout.
  const doneCount = boardItems.filter(i => i.status !== 'Open').filter(boardVisible).length;

  // Cold Theirs items STAY in THEIR COURT (they need a person chased, not
  // work done) — Chads holds only cold Mine/Holding items. Cold treatment
  // (lit, day counter, TODAY) applies either way.
  const columnItems = (key) => {
    let col;
    if (key === 'chads') {
      col = boardItems.filter(i => isCold(i, today) && i.court !== 'Theirs');
    } else if (key === 'Theirs') {
      col = boardItems.filter(i => i.court === 'Theirs');
    } else {
      col = boardItems.filter(i => i.court === key && !isCold(i, today));
    }
    // A card in NOW or TODAY never renders a second time in a column.
    return sortColumn(col.filter(statusShown).filter(boardVisible).filter(i => !tierIds.has(i.id)), sortKey, heatMap, today);
  };
  const backlogCount = COLUMNS.reduce((n, c) => n + columnItems(c.key).length, 0);
  // A NOW / TODAY row opens to the same Card the columns render, body open
  // so Thread (N) and its links are one click away. One row at a time.
  const toggleTierRow = (id) => setTierExpanded(x => (x === id ? null : id));
  const renderTierCard = (i) => (
    <Card item={i} today={today} byId={byId}
      projectColors={projectColors} projectLabels={projectLabels}
      showTouch={i.court === 'Theirs' || i.court === 'Holding'}
      childItems={visibleChildrenOf(i.id)} parentWhys={parentWhysOf[i.id]}
      parentTag={parentTagOf(i)} heatMap={heatMap}
      selectedTags={selectedTags} searchQuery={searchQuery}
      forceExpanded={1} inTier
      onSuggestCourt={suggestCourt} />
  );

  if (loading && items.length === 0) {
    return (
      <div className="Watch">
        <div className="Watch-masthead"><span className="Watch-masthead-title">THE WATCH</span></div>
        <section className="Watch-section">
          <div className="Watch-section-header"><span className="Watch-section-title Watch-section-title--new">NEW</span></div>
          <NewRail loading />
        </section>
      </div>
    );
  }
  if (error)   return <div className="watch-state watch-state--error">{error}</div>;

  return (
    <div className="Watch">
      <div className="Watch-masthead">
        <span className="Watch-masthead-title">THE WATCH</span>
        <span className="Watch-masthead-sub">
          {today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}
          <CountStrip counts={boardCounts} activeCourts={filters.court}
            onCourt={(v) => toggleFilter('court', v)} onNew={revealNewRail} onTotal={() => setFilters(EMPTY_FILTERS)} />
          <span className="Watch-masthead-dim">{' · '}</span>
          {/* The persisted report, not a run: green ok, amber one failure or
              stale in sweep hours, red two failures in a row; no dot before
              the first report. CHECK NOW re-reads the row and reloads the
              board only when a run finished after it last loaded. */}
          {dot && <span className={`Watch-sweep-dot Watch-sweep-dot--${dot}`} data-testid="sweep-dot" aria-label={`sweep ${dot}`}>●</span>}
          <span className="Watch-sweep-ago">{sweepAgo ? `swept ${sweepAgo}` : 'no sweep yet'}</span>
          {sweepCounts && <span className="Watch-masthead-dim">{' '}{sweepCounts}</span>}
          <span className="Watch-masthead-dim">{' · '}</span>
          <button className="Watch-triage-btn Watch-sweep-btn" disabled={checking} aria-busy={checking || undefined}
            onClick={checkNow}>{checking ? 'CHECKING…' : 'CHECK NOW'}</button>
          <span className="Watch-masthead-dim">{' · '}</span>
          <button className="Watch-triage-btn Watch-sweep-btn" onClick={() => setBriefOpen(true)}>BRIEF</button>
          <span className="Watch-masthead-dim">{' · '}</span>
          {/* Phase A: BOARD (NOW / TODAY / columns) or PROJECTS (the tree). SWEEP and NEW show in both. */}
          <span className="Watch-view-toggle" role="group" aria-label="View">
            <button className={'Watch-triage-btn Watch-view-btn' + (view === 'board' ? ' Watch-view-btn--on' : '')} aria-pressed={view === 'board'} onClick={() => setView('board')}>BOARD</button>
            <button className={'Watch-triage-btn Watch-view-btn' + (view === 'projects' ? ' Watch-view-btn--on' : '')} aria-pressed={view === 'projects'} onClick={() => setView('projects')}>PROJECTS</button>
          </span>
        </span>
      </div>
      {sweepAlert && <div className="Watch-sweep-alert" role="alert">{sweepAlert}</div>}
      {projectsError && <div className="Watch-sweep-alert" role="alert">Projects: {projectsError} — showing no project bands until Watch_Projects is fixed.</div>}

      {toast && (toast.onUndo
        ? <UndoToast text={toast.text} ttl={toast.ttl} onUndo={toast.onUndo} />
        : <div className="Watch-jump-toast" role="status">{toast.text}</div>)}

      {briefOpen && (
        <Brief items={items} today={today} parseDate={parseDate} fmtD={fmtD}
          daysSince={(i) => daysSinceTouched(i, today)} isCold={(i) => isCold(i, today)}
          onClose={closeBrief} onJump={jumpToCard} />
      )}

      {/* MARC'S LIST — his own short list, on top of everything the system computes */}
      {view === 'board' && (
        <MyList tasks={myVisible} byWatchId={byId} colorOf={colorOf} colorOfProject={colorOfProject} projects={pickerProjects} error={myListError}
          onCheck={myListCheck} onUncheck={myListUncheck} onNote={myListNote} onDrop={myListDrop} onAdd={myListAdd} onJump={jumpToCard} />
      )}

      {/* NOW — chosen focus, above everything the system computes */}
      {/* NOW — three slots, auto-filled: pins, overdue/check, due/check-soon, Mine by heat */}
      {view === 'board' && <section className="Watch-section">
        <div className="Watch-section-header">
          <span className="Watch-section-title Watch-section-title--now">NOW</span>
        </div>
        <NowTray now={tiers.now} onPin={togglePin} expandedId={tierExpanded} onToggle={toggleTierRow} renderCard={renderTierCard} colorOf={colorOf} />
      </section>}

      {/* TODAY — reason-first, then heat, capped at five; the rest sit in BACKLOG */}
      {view === 'board' && <section className="Watch-section">
        <div className="Watch-section-header Watch-section-header--clickable"
          onClick={() => setTodayOpen(o => !o)}>
          <span className="Watch-section-title Watch-section-title--today">TODAY</span>
          {tiers.todayTotal > 0 && (
            <span className={'Watch-section-count' + (tiers.todayTotal > TODAY_CAP ? ' Watch-section-count--of' : '')}>
              {tiers.todayTotal > TODAY_CAP ? `${tiers.today.length} of ${tiers.todayTotal}` : tiers.today.length}
            </span>
          )}
          <span className="Watch-card-caret">{todayOpen ? '▴' : '▾'}</span>
        </div>
        {todayOpen && <TodayList today={tiers.today} onPin={togglePin} expandedId={tierExpanded} onToggle={toggleTierRow} renderCard={renderTierCard} colorOf={colorOf} />}
      </section>}

      {/* SWEEP — findings rail; local until GO */}
      {(sweep || sweepError) && (
        <section className="Watch-section Watch-sweep-rail">
          <div className="Watch-section-header">
            <span className="Watch-section-title Watch-section-title--new">SWEEP</span>
            {sweep && (
              <span className="Watch-sweep-counts">
                MOVED {shownRows('moved').length} · CLOSED {shownRows('closed').length} · PROPOSED {shownRows('proposed').length} · UNMATCHED {shownRows('unmatched').length}
                <span className="Watch-masthead-dim">{' · since '}{ctStamp(sweep.since?.stored)}{' · read '}{sweep.read?.inbox}/{sweep.read?.sent}</span>
              </span>
            )}
            <span className="Watch-sweep-spacer" />
            {sweep && sweep.dryRun && adjPlanTotal > 0 && (
              sweepArmed ? (
                <>
                  <button className="Watch-triage-btn Watch-triage-btn--go" disabled={sweepBusy}
                    title="Estimate from the last dry run. GO rescans — anything that arrived since is written too."
                    onClick={() => doSweep(false)}>WRITE {adjSources} SRC · {sweep.plan.lastTouched} TOUCHED · +{adjNewOpens} OPENS · EST</button>
                  <span className="Watch-triage-sep">·</span>
                  <button className="Watch-triage-btn" onClick={() => setSweepArmed(false)}>CANCEL</button>
                </>
              ) : (
                <button className="Watch-triage-btn Watch-triage-btn--go" disabled={sweepBusy}
                  onClick={() => setSweepArmed(true)}>GO</button>
              )
            )}
            {sweep && sweep.dryRun && adjPlanTotal === 0 && (
              <span className="Watch-masthead-dim">NOTHING TO WRITE</span>
            )}
            <button className="Watch-action-error-close" title="Close" onClick={() => { setSweep(null); setSweepError(null); }}>×</button>
          </div>

          {sweepError && (
            <div className="Watch-action-error">{sweepError}
              <button className="Watch-action-error-close" onClick={() => setSweepError(null)}>×</button>
            </div>
          )}

          {sweep && (
            <div className="Watch-today-rows">
              {shownRows('moved').map(r => (
                <div key={railKey.moved(r)} className="Watch-today-row">
                  <span className="Watch-sweep-tag">MOVED</span>
                  <span className="Watch-trip-title">#{r.watchId} {r.title}</span>
                  <span className="Watch-trip-court">
                    {r.storedCourt !== r.computedCourt ? `${(r.storedCourt||'').toUpperCase()} → ${(r.computedCourt||'').toUpperCase()}` : (r.computedCourt||'').toUpperCase()}
                    {r.computedTouched > r.storedTouched && <span className="Watch-masthead-dim">{' · touched '}{r.computedTouched}</span>}
                  </span>
                  <span className="Watch-sweep-proving">
                    {r.proving?.from}{' · '}{ctShort(r.proving?.ct)}{' · '}
                    {r.proving?.webLink ? <a href={replyUrl({ webLink: r.proving.webLink, mailbox: sweepMailbox })} target="_blank" rel="noreferrer">{r.proving?.subject}</a> : r.proving?.subject}
                  </span>
                  {courtFor === r.watchId ? (
                    <span className="Watch-attach">
                      <input className="Watch-attach-input" placeholder="with whom" value={courtForm.counterparty}
                        onChange={e => setCourtForm(f => ({ ...f, counterparty: e.target.value }))} />
                      <input className="Watch-attach-input" placeholder="next action (optional)" value={courtForm.nextAction} autoFocus
                        onChange={e => setCourtForm(f => ({ ...f, nextAction: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') commitAcceptCourt(r, courtForm); if (e.key === 'Escape') setCourtFor(null); }} />
                      <span className="Watch-triage-btns">
                        <span className="Watch-masthead-dim">wake</span>
                        <input className="Watch-attach-input Watch-attach-input--date" type="date" value={courtForm.wakeDate}
                          onChange={e => setCourtForm(f => ({ ...f, wakeDate: e.target.value }))} />
                        <button className="Watch-triage-btn Watch-triage-btn--go" disabled={busyIds.has(r.watchId)}
                          onClick={() => commitAcceptCourt(r, courtForm)}>ACCEPT → {(r.computedCourt || '').toUpperCase()}</button>
                        <span className="Watch-triage-sep">·</span>
                        <button className="Watch-triage-btn" onClick={() => setCourtFor(null)}>CANCEL</button>
                      </span>
                    </span>
                  ) : (
                    <span className="Watch-triage-btns">
                      {r.storedCourt !== r.computedCourt && (
                        <>
                          <button className="Watch-triage-btn" disabled={busyIds.has(r.watchId)} onClick={() => openAcceptCourt(r)}>ACCEPT COURT</button>
                          <span className="Watch-triage-sep">·</span>
                        </>
                      )}
                      <button className="Watch-triage-btn" onClick={() => dismissRow(railKey.moved(r))}>DISMISS</button>
                    </span>
                  )}
                </div>
              ))}

              {shownRows('closed').map(r => (
                <div key={railKey.closed(r)} className="Watch-today-row">
                  <span className="Watch-sweep-tag Watch-sweep-tag--closed">CLOSED?</span>
                  <span className="Watch-trip-title">#{r.watchId} {r.title}</span>
                  {r.nextAction && <span className="Watch-trip-next">{r.nextAction}</span>}
                  <span className="Watch-sweep-proving">
                    {'you · '}{ctShort(r.proving?.ct)}{' · '}
                    {r.proving?.webLink ? <a href={replyUrl({ webLink: r.proving.webLink, mailbox: sweepMailbox })} target="_blank" rel="noreferrer">{r.proving?.subject}</a> : r.proving?.subject}
                  </span>
                  <span className="Watch-triage-btns">
                    <button className="Watch-triage-btn" onClick={() => dismissRow(railKey.closed(r))}>DISMISS</button>
                  </span>
                </div>
              ))}

              {shownRows('proposed').length > 0 && (
                <NewRail mode="proposal" today={today} tree={projects}
                  rows={shownRows('proposed').map(r => rowFromProposal(r, byId, projects))}
                  busyKeys={new Set(shownRows('proposed').filter(r => attachFor === r.key && attachTarget && busyIds.has(Number(attachTarget))).map(r => `p:${r.key}`))}
                  onAction={(row, action) => {
                    const r = row.proposal;
                    if (action === 'dismiss') { dismissRow(railKey.proposed(r)); if (attachFor === r.key) setAttachFor(null); }
                    if (action === 'attachPick') { setAttachFor(attachFor === r.key ? null : r.key); setAttachTarget(''); setAttachQuery(''); }
                  }}
                  onAttach={(row, wid) => doAttach(row.proposal, wid)}
                  onKillAll={(rows) => rows.forEach(row => dismissRow(railKey.proposed(row.proposal)))}
                  renderExtra={(row) => attachFor === row.proposal.key ? (
                    <span className="Watch-attach">
                      <input className="Watch-attach-input" autoFocus placeholder="type to find an Open…"
                        value={attachQuery}
                        onChange={e => { setAttachQuery(e.target.value); setAttachTarget(''); }}
                        onKeyDown={e => {
                          if (e.key === 'Escape') { setAttachFor(null); setAttachTarget(''); setAttachQuery(''); }
                          if (e.key === 'Enter') { const c = attachCandidates(items, row.proposal, attachQuery, labelOf)[0]; if (c) setAttachTarget(String(c.id)); }
                        }} />
                      <div className="Watch-attach-list">
                        {attachCandidates(items, row.proposal, attachQuery, labelOf).map(i => (
                          <div key={i.id}
                            className={'Watch-attach-row' + (String(i.id) === attachTarget ? ' Watch-attach-row--sel' : '')}
                            onClick={() => setAttachTarget(String(i.id))}>
                            <span className="Watch-trip-dot" style={{ background: projectColors[i.projectId || NO_PROJECT] }} />
                            <span className="Watch-attach-id">#{i.id}</span>
                            <span className="Watch-attach-title">{i.title}</span>
                            {i.insuredName && <span className="Watch-attach-ins">{i.insuredName}</span>}
                          </div>
                        ))}
                        {attachCandidates(items, row.proposal, attachQuery, labelOf).length === 0 && <div className="Watch-attach-empty">no match</div>}
                      </div>
                      <span className="Watch-triage-btns">
                        <button className="Watch-triage-btn" disabled={!attachTarget || busyIds.has(Number(attachTarget))} onClick={() => doAttach(row.proposal)}>{attachTarget ? `Attach → #${attachTarget}` : 'Attach'}</button>
                        <span className="Watch-triage-sep">·</span>
                        <button className="Watch-triage-btn" onClick={() => { setAttachFor(null); setAttachTarget(''); setAttachQuery(''); }}>Cancel</button>
                      </span>
                    </span>
                  ) : null} />
              )}

              {shownRows('unmatched').length > 0 && (
                <div className="Watch-today-row Watch-sweep-row--dim" onClick={() => setUnmatchedOpen(o => !o)} style={{ cursor: 'pointer' }}>
                  <span className="Watch-sweep-tag Watch-sweep-tag--dim">UNMATCHED</span>
                  <span className="Watch-trip-title">{shownRows('unmatched').length} dropped</span>
                  <span className="Watch-card-caret">{unmatchedOpen ? '▴' : '▾'}</span>
                </div>
              )}
              {unmatchedOpen && shownRows('unmatched').map(r => (
                <div key={railKey.unmatched(r)} className="Watch-today-row Watch-sweep-row--dim">
                  <span className="Watch-sweep-tag Watch-sweep-tag--dim" />
                  <span className="Watch-trip-title">{r.subject}</span>
                  <span className="Watch-sweep-proving">{r.from}{' · '}{ctShort(r.ct)}{' · '}{r.reason}</span>
                  <span className="Watch-triage-btns">
                    <button className="Watch-triage-btn" onClick={() => dismissRow(railKey.unmatched(r))}>DISMISS</button>
                  </span>
                </div>
              ))}

              {shownRows('moved').length + shownRows('closed').length + shownRows('proposed').length + shownRows('unmatched').length === 0 && (
                <div className="Watch-empty">
                  {sweep.moved.length + sweep.closed.length + sweep.proposed.length + sweep.unmatched.length === 0
                    ? (sweep.oneLiners?.['(all)'] || 'Nothing new since last sweep.')
                    : 'All rows dismissed.'}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* CHECK — sender-only attaches from the last sweep; ✓ dismisses, MOVE re-files the thread */}
      {checkRows.length > 0 && (
        <section className="Watch-section" data-testid="check-section">
          <div className="Watch-section-header">
            <span className="Watch-section-title Watch-section-title--new">CHECK</span>
            <span className="Watch-section-count">{checkRows.length}</span>
            {checkReduced && <span className="Watch-masthead-dim">{' · from the last sweep'}</span>}
          </div>
          <CheckRail rows={checkRows} reduced={checkReduced} byId={byId} items={items}
            busyKeys={new Set(checkRows.filter(r => busyIds.has(r.watchId)).map(checkKey))}
            onDismiss={(r) => setCheckDismissed(s => new Set(s).add(checkKey(r)))} onMove={moveCheck} />
        </section>
      )}

      {/* NEW — untriaged arrivals. The light rail; empty state is a line, not a box. */}
      <section className={'Watch-section' + (railPulse ? ' Watch-section--pulse' : '')} ref={newRailRef} id="watch-new-rail">
        <div className="Watch-section-header">
          <span className="Watch-section-title Watch-section-title--new">NEW</span>
          {newItems.length > 0 && <span className="Watch-section-count">{newItems.length}</span>}
        </div>
        <NewRail mode="triage" today={today} loading={loading && newItems.length === 0} tree={projects} onProject={setRowProject}
          rows={newItems.map(i => rowFromItem(i, byId, projects))}
          busyKeys={new Set(newItems.filter(i => busyIds.has(i.id)).map(i => `n:${i.id}`))}
          onAction={(row, action, payload) => disposition(byId.get(row.id), action, payload)}
          onAttach={(row, wid) => mergeInto(byId.get(row.id), wid)}
          onKillAll={(rows) => killAll(rows.map(row => byId.get(row.id)))}
          error={actionError} onClearError={() => setActionError(null)} />
      </section>

      {/* PROJECTS — the tree, every card under its project; read-only in Phase A */}
      {view === 'projects' && (
        <section className="Watch-section">
          <div className="Watch-section-header">
            <span className="Watch-section-title">PROJECTS</span>
            <span className="Watch-section-count">{projects.roots.length}</span>
          </div>
          {/* EVERY item, not boardItems: the board's parent/child collapse is a
              board layout rule; here a folded child is its own row. tierOf
              still comes from tier(boardItems), so NOW / TODAY match the board. */}
          <ProjectSearch items={items} labelOf={labelOf} colorOf={colorOf}
            onPick={(id) => { if (view !== 'projects') setView('projects'); setLocate({ id, n: Date.now() }); }} />
          <ProjectsView items={items} tree={projects} tierOf={tierOf} heatMap={heatMap} colorOf={colorOf}
            onPin={togglePin} expandedId={tierExpanded} onToggle={toggleTierRow} renderCard={renderTierCard}
            onMove={moveCard} onRename={renameCard} onMerge={mergeCard} onRenameProject={renameProject} onCreateProject={createProject}
            locate={locate} />
        </section>
      )}

      {/* SAME-TRIP callouts */}
      {view === 'board' && tripGroups.map((g, i) => (
        <TripCallout key={i} group={g} today={today} projectColors={projectColors} />
      ))}

      {/* THE BOARD */}
      {view === 'board' && <section className="Watch-section">
        <div className="Watch-section-header">
          <span className="Watch-section-title">THE BOARD</span>
        </div>

        {/* SORT + FILTER — display controls only; the seed is never touched */}
        <div className="Watch-controls">
          <div className="Watch-controls-sort">
            <span className="Watch-controls-label">SORT</span>
            <select className="Watch-sort-select" value={sortKey}
              onChange={e => setSortKey(e.target.value)}>
              {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <div className="Watch-search">
              <input className="Watch-search-input" type="text" placeholder="SEARCH"
                value={searchText} onChange={e => setSearchText(e.target.value)} />
              {searchText !== '' && (
                <button className="Watch-search-clear" title="Clear search"
                  onClick={() => setSearchText('')}>×</button>
              )}
            </div>
            {searchQuery !== '' && (
              <span className="Watch-controls-count">{searchMatchCount} MATCH</span>
            )}
          </div>
          <div className="Watch-controls-chips">
            {FILTER_FIELDS.map(f => filterValues[f].length > 0 && (
              <span key={f} className="Watch-filter-group">
                <span className="Watch-controls-label">{FILTER_LABELS[f]}</span>
                {filterValues[f].map(v => (
                  <button key={v || '(none)'}
                    className={'Watch-filter-chip' + (filters[f].includes(v) ? ' Watch-filter-chip--on' : '')}
                    style={f === 'projectId' ? { '--chip-color': projectColors[v] } : undefined}
                    onClick={() => toggleFilter(f, v)}>
                    {f === 'projectId' ? (projectLabels[v] || '—').toUpperCase() : String(v).toUpperCase()}
                  </button>
                ))}
              </span>
            ))}
            <span className="Watch-filter-group">
              <button className="Watch-filter-chip Watch-filter-chip--clear"
                onClick={() => setFilters(EMPTY_FILTERS)}>
                CLEAR
              </button>
              <span className="Watch-controls-count">{visibleCount} CARDS · {items.length} ITEMS</span>
            </span>
            <span className="Watch-showdone-wrap">
              <button className="Watch-showdone" aria-pressed={showDone}
                onClick={() => setShowDone(d => !d)}>
                <span className={'Watch-showdone-box' + (showDone ? ' Watch-showdone-box--on' : '')} />
                SHOW DONE · {doneCount} {showDone ? 'SHOWN' : 'HIDDEN'}
              </button>
            </span>
          </div>
          {/* TAGS — independent mechanic: dims non-matching cards, hides nothing */}
          <div className="Watch-controls-chips">
            <span className="Watch-filter-group">
              <span className="Watch-controls-label">TAGS</span>
              {allTags.map(t => (
                <button key={t}
                  className={'Watch-tag-chip Watch-tag-chip--btn' + (selectedTags.includes(t) ? ' Watch-tag-chip--on' : '')}
                  onClick={() => toggleTag(t)}>
                  {String(t).toUpperCase()}
                </button>
              ))}
            </span>
            <span className="Watch-filter-group">
              <button className="Watch-filter-chip Watch-filter-chip--clear"
                onClick={() => setSelectedTags([])}>
                CLEAR TAGS
              </button>
              {selectedTags.length > 0 && (
                <span className="Watch-controls-count">{tagMatchCount} MATCH</span>
              )}
            </span>
          </div>
        </div>

        {/* BACKLOG — the four columns, collapsed by default; NOW and TODAY cards are excluded */}
        <div className="Watch-section-header Watch-section-header--clickable Watch-backlog-header"
          onClick={() => setBacklogOpen(o => !o)}>
          <span className="Watch-section-title">BACKLOG</span>
          <span className="Watch-section-count">{backlogCount}</span>
          <span className="Watch-card-caret">{backlogOpen ? '▴' : '▾'}</span>
        </div>
        {backlogOpen && <div className="Watch-board">
          {COLUMNS.map(col => {
            const colItems = columnItems(col.key);
            return (
              <div key={col.key} className={'Watch-col' + (col.key === 'chads' ? ' Watch-col--chads' : '')}>
                <div className="Watch-col-header">
                  <span className="Watch-col-title">{col.label}</span>
                  <span className="Watch-col-count">{colItems.length}</span>
                </div>
                {colItems.length === 0 && <div className="Watch-col-empty">—</div>}
                {colItems.map(i => (
                  <Card key={i.id} item={i} today={today} byId={byId}
                    projectColors={projectColors} projectLabels={projectLabels}
                    showTouch={i.court === 'Theirs' || i.court === 'Holding'}
                    childItems={visibleChildrenOf(i.id)} parentWhys={parentWhysOf[i.id]}
                    parentTag={parentTagOf(i)} heatMap={heatMap}
                    selectedTags={selectedTags} searchQuery={searchQuery}
                    forceExpanded={expandReq && expandReq.id === i.id ? expandReq.n : 0}
                    onSuggestCourt={suggestCourt} />
                ))}
              </div>
            );
          })}
        </div>}
      </section>}
    </div>
  );
}
